import axios from "axios";
import { API } from "./api";

const ADMIN_TOKEN_KEY = "outlapped.admin.token";

export function getAdminToken() {
    return window.localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminToken(token) {
    window.localStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearAdminToken() {
    window.localStorage.removeItem(ADMIN_TOKEN_KEY);
}

const adminHttp = axios.create({
    baseURL: API,
    timeout: 10000,
});

adminHttp.interceptors.request.use((config) => {
    const token = getAdminToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

function collectionClient(path) {
    return {
        list: (params = {}) =>
            adminHttp.get(`/admin/${path}`, { params }).then((response) => response.data.items),
        get: (id) => adminHttp.get(`/admin/${path}/${id}`).then((response) => response.data),
        create: (payload) => adminHttp.post(`/admin/${path}`, payload).then((response) => response.data),
        update: (id, payload) =>
            adminHttp.put(`/admin/${path}/${id}`, payload).then((response) => response.data),
        remove: (id) =>
            adminHttp.delete(`/admin/${path}/${id}`).then((response) => response.data),
    };
}

const newsClient = collectionClient("news");
const driversClient = collectionClient("drivers");
const teamsClient = collectionClient("teams");
const calendarClient = collectionClient("calendar");
const recordsClient = collectionClient("records");
const glossaryClient = collectionClient("glossary");
const featuredClient = collectionClient("featured");

export const adminApi = {
    login: async (payload) => {
        const response = await adminHttp.post("/admin/auth/login", payload);
        if (response.data?.token) {
            setAdminToken(response.data.token);
        }
        return response.data;
    },
    session: () => adminHttp.get("/admin/auth/session").then((response) => response.data),
    logout: async () => {
        try {
            await adminHttp.post("/admin/auth/logout");
        } finally {
            clearAdminToken();
        }
    },
    summary: () => adminHttp.get("/admin/summary").then((response) => response.data),
    news: {
        ...newsClient,
        publish: (id, status) =>
            adminHttp.patch(`/admin/news/${id}/publish`, { status }).then((response) => response.data),
        feature: (id, featured) =>
            adminHttp.patch(`/admin/news/${id}/feature`, { featured }).then((response) => response.data),
    },
    drivers: driversClient,
    teams: teamsClient,
    calendar: {
        ...calendarClient,
        refresh: () =>
            adminHttp.post("/admin/calendar/refresh").then((response) => response.data),
    },
    records: recordsClient,
    glossary: glossaryClient,
    featured: featuredClient,
    providers: {
        list: () => adminHttp.get("/admin/providers").then((response) => response.data.items),
        health: () => adminHttp.get("/admin/providers/health").then((response) => response.data),
        jobs: (params = {}) => adminHttp.get("/admin/providers/jobs", { params }).then((response) => response.data),
        runJob: (payload) => adminHttp.post("/admin/providers/jobs/run", payload).then((response) => response.data),
        retryJob: (payload) => adminHttp.post("/admin/providers/jobs/retry", payload).then((response) => response.data),
        cancelJob: (payload) => adminHttp.post("/admin/providers/jobs/cancel", payload).then((response) => response.data),
        queue: () => adminHttp.get("/admin/providers/jobs/queue").then((response) => response.data),
        locks: () => adminHttp.get("/admin/providers/jobs/locks").then((response) => response.data),
        sync: (payload) => adminHttp.post("/admin/providers/sync", payload).then((response) => response.data),
        update: (providerName, payload) =>
            adminHttp.put(`/admin/providers/${providerName}`, payload).then((response) => response.data),
        deleteKey: (providerName) =>
            adminHttp.delete(`/admin/providers/${providerName}/key`).then((response) => response.data),
    },
    system: {
        readiness: () => adminHttp.get("/health/readiness").then((response) => response.data),
        health: () => adminHttp.get("/admin/system/health").then((response) => response.data),
        metrics: () => adminHttp.get("/admin/system/metrics").then((response) => response.data),
        events: (params = {}) => adminHttp.get("/admin/system/events", { params }).then((response) => response.data),
        warnings: () => adminHttp.get("/admin/system/warnings").then((response) => response.data),
        runDiagnostics: () => adminHttp.post("/admin/system/diagnostics/run").then((response) => response.data),
    },
    liveOps: {
        status: () => adminHttp.get("/admin/live/status").then((response) => response.data),
        health: () => adminHttp.get("/race/live/health").then((response) => response.data),
        refreshAnalysis: (payload = {}) =>
            adminHttp.post("/admin/live/refresh-analysis", payload).then((response) => response.data),
        startSimulation: () =>
            adminHttp.post("/admin/live/simulation/start").then((response) => response.data),
        stopSimulation: () =>
            adminHttp.post("/admin/live/simulation/stop").then((response) => response.data),
        testProvider: (payload = {}) =>
            adminHttp.post("/admin/live/provider/test", payload).then((response) => response.data),
    },
    replayOps: {
        bundles: () => adminHttp.get("/admin/replay/bundles").then((response) => response.data),
        diagnostics: () => adminHttp.get("/admin/replay/diagnostics").then((response) => response.data),
        jobs: (params = {}) => adminHttp.get("/admin/replay/jobs", { params }).then((response) => response.data),
        job: (jobId) => adminHttp.get(`/admin/replay/jobs/${jobId}`).then((response) => response.data),
        queueGeneration: (payload = {}) =>
            adminHttp.post("/admin/replay/generate", payload).then((response) => response.data),
        cancelJob: (jobId) =>
            adminHttp.post(`/admin/replay/jobs/${jobId}/cancel`).then((response) => response.data),
        bundleQa: (bundleId) => adminHttp.get(`/admin/replay/bundles/${bundleId}/qa`).then((response) => response.data),
        regenerateBundle: (bundleId, payload = {}) =>
            adminHttp.post(`/admin/replay/bundles/${bundleId}/regenerate`, payload).then((response) => response.data),
        generateRaceIq: (bundleId, payload = {}) =>
            adminHttp.post(`/admin/replay/bundles/${bundleId}/generate-race-iq`, payload).then((response) => response.data),
    },
    raceEngine: {
        inspector: () => adminHttp.get("/race-engine/admin/inspector").then((response) => response.data),
        rebuildReplay: (sessionKey, { force = false } = {}) =>
            adminHttp
                .post(`/race-engine/admin/sessions/${sessionKey}/rebuild-replay`, null, { params: { force }, timeout: 180000 })
                .then((response) => response.data),
        resync: (sessionKey) => adminHttp.post(`/race-engine/admin/sessions/${sessionKey}/resync`).then((response) => response.data),
    },
    sync: {
        schedule: () => adminHttp.post("/admin/sync/schedule").then((response) => response.data),
        drivers: () => adminHttp.post("/admin/sync/drivers").then((response) => response.data),
        teams: () => adminHttp.post("/admin/sync/teams").then((response) => response.data),
        standings: () => adminHttp.post("/admin/sync/standings").then((response) => response.data),
        news: () => adminHttp.post("/admin/sync/news").then((response) => response.data),
        all: () => adminHttp.post("/admin/sync/all").then((response) => response.data),
        recomputeStats: () => adminHttp.post("/admin/stats/recompute").then((response) => response.data),
        logs: () => adminHttp.get("/admin/sync/logs").then((response) => response.data.items),
    },
    auditLogs: () => adminHttp.get("/admin/audit-logs").then((response) => response.data.items),
    getReadiness: () => adminHttp.get("/health/readiness").then((response) => response.data),
    getAdminLiveStatus: () => adminHttp.get("/admin/live/status").then((response) => response.data),
    refreshAdminLiveAnalysis: (payload = {}) =>
        adminHttp.post("/admin/live/refresh-analysis", payload).then((response) => response.data),
    startLiveSimulation: () => adminHttp.post("/admin/live/simulation/start").then((response) => response.data),
    stopLiveSimulation: () => adminHttp.post("/admin/live/simulation/stop").then((response) => response.data),
    testLiveProvider: (payload = {}) => adminHttp.post("/admin/live/provider/test", payload).then((response) => response.data),
    listReplayJobs: (params = {}) => adminHttp.get("/admin/replay/jobs", { params }).then((response) => response.data),
    getReplayJob: (jobId) => adminHttp.get(`/admin/replay/jobs/${jobId}`).then((response) => response.data),
    queueReplayGeneration: (payload = {}) => adminHttp.post("/admin/replay/generate", payload).then((response) => response.data),
    cancelReplayJob: (jobId) => adminHttp.post(`/admin/replay/jobs/${jobId}/cancel`).then((response) => response.data),
    settings: {
        get: () => adminHttp.get("/admin/settings").then((response) => response.data),
        update: (payload) =>
            adminHttp.put("/admin/settings", payload).then((response) => response.data),
    },
};
